// Single source of truth for Southeast Asia Luxury Yachts marketplace categories.
// Every top-level section has a slug, title, short description, hero image and subcategories.
// Icons are lucide-react names (imported where needed).

export const CATEGORIES = {
  'yachts-for-sale': {
    slug: 'yachts-for-sale',
    title: 'Yachts for Sale',
    short: 'Buy',
    tagline: 'Own your next chapter on the water.',
    description:
      'A curated fleet of vessels for private ownership — from agile day boats to 80m+ superyachts.',
    hero: 'https://images.unsplash.com/photo-1775700489260-41ee85c86954?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85',
    icon: 'Anchor',
    listingType: 'sale',
    subcategories: [
      {
        slug: 'motor-yachts',
        name: 'Motor Yachts',
        icon: 'Ship',
        description: 'Powerful cruisers built for speed, comfort and range.',
        image:
          'https://images.unsplash.com/photo-1768740615503-52872b6707b1?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
        match: { yacht_type: 'power' },
      },
      {
        slug: 'sailing-yachts',
        name: 'Sailing Yachts',
        icon: 'Sailboat',
        description: 'Classic and contemporary sail yachts for timeless passages.',
        image:
          'https://images.unsplash.com/photo-1590218087440-df21ca448c26?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
        match: { yacht_type: 'sail' },
      },
      {
        slug: 'expedition-yachts',
        name: 'Expedition Yachts',
        icon: 'Compass',
        description: 'Ocean-crossing explorers built for remote waters and long range.',
        image:
          'https://images.unsplash.com/photo-1633123454005-28016323abc2?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
        match: { yacht_type: 'power', min_length_ft: 90 },
      },
      {
        slug: 'catamarans-multihulls',
        name: 'Catamarans & Multihulls',
        icon: 'Waves',
        description: 'Stable twin-hull performance with palatial living space.',
        image:
          'https://images.pexels.com/photos/35609576/pexels-photo-35609576.jpeg?auto=compress&cs=tinysrgb&w=1200',
        match: { yacht_type: 'catamaran' },
      },
      {
        slug: 'superyachts',
        name: 'Superyachts',
        icon: 'Crown',
        description: 'Icons of engineering — vessels 100ft and above, fully crewed.',
        image:
          'https://images.unsplash.com/photo-1641787424891-63baaf4eef09?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
        match: { yacht_type: 'superyacht' },
      },
      {
        slug: 'classic-vintage',
        name: 'Classic & Vintage Yachts',
        icon: 'Landmark',
        description: 'Restored classics, wooden hulls, storied provenance.',
        image:
          'https://images.unsplash.com/photo-1699619712129-08df80815428?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
      },
      {
        slug: 'sportfishing',
        name: 'Sportfishing Yachts',
        icon: 'Fish',
        description: 'Tournament-grade convertibles built for the blue water chase.',
        image:
          'https://images.unsplash.com/photo-1771962760095-1db83de4cb0f?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
      },
      {
        slug: 'day-boats-tenders',
        name: 'Day Boats & Tenders',
        icon: 'Sailboat',
        description: 'Chase boats, center consoles and tenders for the perfect day afloat.',
        image:
          'https://images.unsplash.com/photo-1596480330200-5794a8d4c942?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
        match: { max_length_ft: 40 },
      },
    ],
  },

  'yacht-charters': {
    slug: 'yacht-charters',
    title: 'Yacht Charters',
    short: 'Charter',
    tagline: 'Bespoke voyages across Southeast Asia.',
    description:
      'Crewed, bareboat and expedition charters tailored to Phuket, the Andaman, Langkawi and beyond.',
    hero: 'https://images.unsplash.com/photo-1698666348132-fa9121fd6d19?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85',
    icon: 'Ship',
    listingType: 'charter',
    subcategories: [
      {
        slug: 'crewed-charters',
        name: 'Crewed Charters',
        icon: 'Users',
        description: 'Fully staffed voyages with captain, chef and stewardesses.',
        image:
          'https://images.unsplash.com/photo-1686834097692-8beb8917385e?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
      },
      {
        slug: 'bareboat-charters',
        name: 'Bareboat Charters',
        icon: 'Key',
        description: 'Skipper your own adventure — licensed captains welcome.',
        image:
          'https://images.unsplash.com/photo-1650297759706-f066824fa3dc?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
      },
      {
        slug: 'day-charters',
        name: 'Day Charters',
        icon: 'Sun',
        description: 'Sunset cruises and island-hop days departing Phuket & Langkawi.',
        image:
          'https://images.pexels.com/photos/27498639/pexels-photo-27498639.jpeg?auto=compress&cs=tinysrgb&w=1200',
      },
      {
        slug: 'weekly-charters',
        name: 'Weekly Charters',
        icon: 'CalendarDays',
        description: 'Seven-day itineraries through the Similans, Phi Phi and Mergui.',
        image:
          'https://images.unsplash.com/photo-1647534398644-3deed08837b5?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
      },
      {
        slug: 'expedition-charters',
        name: 'Expedition Charters',
        icon: 'Compass',
        description: 'Remote Indonesia, Raja Ampat and the Mergui Archipelago.',
        image:
          'https://images.unsplash.com/photo-1633123454005-28016323abc2?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
      },
      {
        slug: 'phuket-andaman',
        name: 'Phuket & Andaman Charters',
        icon: 'MapPin',
        description: 'Region specialists — Phuket, Phang Nga Bay, Langkawi, Myanmar.',
        image:
          'https://images.unsplash.com/photo-1596658817095-21feff6f586f?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
      },
    ],
  },

  'shop': {
    slug: 'shop',
    title: 'Shop Parts & Accessories',
    short: 'Shop',
    tagline: 'Everything your vessel needs.',
    description:
      'Engines, electronics, safety gear and chandlery from trusted marine brands — shipped across Southeast Asia.',
    hero: 'https://images.unsplash.com/photo-1523559094051-53bac879eb80?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85',
    icon: 'ShoppingBag',
    subcategories: [
      {
        slug: 'engines-propulsion',
        name: 'Engines & Propulsion',
        icon: 'Cog',
        description: 'Inboards, outboards, pods, props and sterndrives.',
        image:
          'https://images.unsplash.com/photo-1552656967-7a0991a13906?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
      },
      {
        slug: 'navigation-electronics',
        name: 'Navigation & Electronics',
        icon: 'Radar',
        description: 'Chartplotters, radar, autopilots, AIS and marine comms.',
        image:
          'https://images.unsplash.com/photo-1776347568367-e8f56443648f?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
      },
      {
        slug: 'safety-gear',
        name: 'Safety Gear',
        icon: 'LifeBuoy',
        description: 'Liferafts, EPIRBs, lifejackets, flares and first-aid.',
        image:
          'https://images.pexels.com/photos/8669061/pexels-photo-8669061.jpeg?auto=compress&cs=tinysrgb&w=1200',
        badge: 'MCA Approved',
      },
      {
        slug: 'anchoring-mooring',
        name: 'Anchoring & Mooring',
        icon: 'Anchor',
        description: 'Anchors, chain, windlasses, mooring lines and fenders.',
        image:
          'https://images.unsplash.com/photo-1601919972568-768b9bbcf548?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
      },
      {
        slug: 'deck-equipment',
        name: 'Deck Equipment',
        icon: 'Wrench',
        description: 'Winches, cleats, davits, hatches and hardware.',
        image:
          'https://images.pexels.com/photos/16135856/pexels-photo-16135856.jpeg?auto=compress&cs=tinysrgb&w=1200',
      },
      {
        slug: 'water-toys-tenders',
        name: 'Water Toys & Tenders',
        icon: 'Waves',
        description: 'Seabobs, jet skis, paddleboards, inflatables and tenders.',
        image:
          'https://images.pexels.com/photos/10256590/pexels-photo-10256590.jpeg?auto=compress&cs=tinysrgb&w=1200',
      },
      {
        slug: 'interior-galley',
        name: 'Interior & Galley',
        icon: 'UtensilsCrossed',
        description: 'Galley appliances, linens, tableware and interior finishes.',
        image:
          'https://images.unsplash.com/photo-1596480330200-5794a8d4c942?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
      },
      {
        slug: 'maintenance-cleaning',
        name: 'Maintenance & Cleaning',
        icon: 'SprayCan',
        description: 'Waxes, polishes, antifouling and detailing supplies.',
        image:
          'https://images.unsplash.com/photo-1739973276975-e013eba151aa?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
      },
      {
        slug: 'electrical-plumbing',
        name: 'Electrical & Plumbing',
        icon: 'Zap',
        description: 'Batteries, inverters, pumps, plumbing and wiring.',
        image:
          'https://images.unsplash.com/photo-1523559094051-53bac879eb80?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
      },
    ],
  },

  'services': {
    slug: 'services',
    title: 'Services',
    short: 'Services',
    tagline: 'Trusted marine professionals.',
    description:
      'Vetted yards, crew, surveyors, insurers and marinas across Phuket, Langkawi, Singapore and beyond.',
    hero: 'https://images.unsplash.com/photo-1700563061894-99a4aa83d048?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85',
    icon: 'Wrench',
    subcategories: [
      {
        slug: 'maintenance-refit',
        name: 'Yacht Maintenance & Refit',
        icon: 'Wrench',
        description: 'Hull work, repowers, interior refits and annual servicing.',
        image:
          'https://images.pexels.com/photos/16135856/pexels-photo-16135856.jpeg?auto=compress&cs=tinysrgb&w=1200',
      },
      {
        slug: 'crew-recruitment',
        name: 'Crew Recruitment & Placement',
        icon: 'Users',
        description: 'Permanent crew placement for private and charter programmes.',
        image:
          'https://images.unsplash.com/photo-1700563061894-99a4aa83d048?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
      },
      {
        slug: 'captain-crew-services',
        name: 'Captain & Crew Services',
        icon: 'UserCheck',
        description: 'Delivery captains, freelance crew and relief cover.',
        image:
          'https://images.unsplash.com/photo-1686834097692-8beb8917385e?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
      },
      {
        slug: 'insurance-finance',
        name: 'Insurance & Finance',
        icon: 'Shield',
        description: 'Hull insurance, charter liability, marine finance and leasing.',
        image:
          'https://images.unsplash.com/photo-1633123454005-28016323abc2?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
      },
      {
        slug: 'survey-valuation',
        name: 'Survey & Valuation',
        icon: 'ClipboardCheck',
        description: 'Pre-purchase, insurance and valuation surveys by accredited surveyors.',
        image:
          'https://images.unsplash.com/photo-1576725198547-2ce386c431fd?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
      },
      {
        slug: 'marina-berthing',
        name: 'Marina Berthing & Storage',
        icon: 'Warehouse',
        description: 'Berth rentals, dry storage and long-term mooring across SEA.',
        image:
          'https://images.pexels.com/photos/14282669/pexels-photo-14282669.jpeg?auto=compress&cs=tinysrgb&w=1200',
      },
      {
        slug: 'transport-delivery',
        name: 'Transport & Delivery',
        icon: 'Truck',
        description: 'Yacht deliveries, transport-on-ship and logistics management.',
        image:
          'https://images.unsplash.com/photo-1759950968587-8ac44e499dfa?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80',
      },
    ],
  },

  'guides': {
    slug: 'guides',
    title: 'Guides & Resources',
    short: 'Guides',
    tagline: 'Knowledge from the helm.',
    description:
      'In-depth editorial for buyers, owners and charter guests — from hull design to ownership economics.',
    hero: 'https://images.unsplash.com/photo-1596658817095-21feff6f586f?crop=entropy&cs=srgb&fm=jpg&w=1920&q=85',
    icon: 'BookOpen',
    // `external` links go directly to existing guide pages
    subcategories: [
      { slug: '/guides/essentials', name: 'Yacht Essentials', icon: 'BookOpen', external: true, description: 'Definitions, anatomy, terminology.' },
      { slug: '/guides/types-propulsion', name: 'Types by Propulsion', icon: 'Cog', external: true, description: 'Power, sail, hybrid, electric.' },
      { slug: '/guides/types-purpose', name: 'Types by Purpose', icon: 'Compass', external: true, description: 'Charter, cruising, sportfishing, expedition.' },
      { slug: '/guides/hull-design', name: 'Hull Design', icon: 'Waves', external: true, description: 'Monohull vs multihull, displacement, planing.' },
      { slug: '/guides/buying-ownership', name: 'Buying & Ownership', icon: 'KeyRound', external: true, description: 'Surveys, flags, VAT, running costs.' },
      { slug: '/guides/maintenance-safety', name: 'Maintenance & Safety', icon: 'ShieldCheck', external: true, description: 'Schedules, haul-outs, safety at sea.' },
      { slug: '/guides/trends-facts', name: 'Trends & Facts', icon: 'TrendingUp', external: true, description: 'Market data, sustainability, new tech.' },
    ],
  },
};

export const CATEGORY_ORDER = [
  'yachts-for-sale',
  'yacht-charters',
  'shop',
  'services',
  'guides',
];

// Helpers ------------------------------------------------------------------

export const getCategory = (slug) => CATEGORIES[slug];

export const getSubcategory = (categorySlug, subSlug) => {
  const cat = CATEGORIES[categorySlug];
  if (!cat) return null;
  return cat.subcategories.find((s) => s.slug === subSlug) || null;
};

// Helper to build a URL for a category / subcategory
export const categoryUrl = (categorySlug, subSlug) => {
  if (categorySlug === 'guides') {
    return subSlug || '/guides';
  }
  if (!subSlug) return `/category/${categorySlug}`;
  return `/category/${categorySlug}/${subSlug}`;
};

// Placeholder product / service data for shop & services subcategories --------

const usd = (n) => `$${n.toLocaleString()}`;

export const SHOP_ITEMS = {
  'engines-propulsion': [
    { id: 'ep-1', name: 'Volvo Penta IPS 950 Package', brand: 'Volvo Penta', price: 89500, priceLabel: usd(89500), image: 'https://images.unsplash.com/photo-1523559094051-53bac879eb80?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.9, reviews: 42, badge: 'OEM' },
    { id: 'ep-2', name: 'Yanmar 8LV-370 Marine Diesel', brand: 'Yanmar', price: 42300, priceLabel: usd(42300), image: 'https://images.unsplash.com/photo-1552656967-7a0991a13906?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.8, reviews: 31 },
    { id: 'ep-3', name: 'Mercury Verado 400 V10 Outboard', brand: 'Mercury', price: 38900, priceLabel: usd(38900), image: 'https://images.unsplash.com/photo-1739973276975-e013eba151aa?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.9, reviews: 57, badge: 'Best Seller' },
    { id: 'ep-4', name: 'Stainless 5-Blade Propeller Set', brand: 'Rolla', price: 4800, priceLabel: usd(4800), image: 'https://images.pexels.com/photos/8669061/pexels-photo-8669061.jpeg?auto=compress&cs=tinysrgb&w=1000', rating: 4.7, reviews: 23 },
  ],
  'navigation-electronics': [
    { id: 'ne-1', name: 'Garmin GPSMAP 8624 Chartplotter', brand: 'Garmin', price: 6700, priceLabel: usd(6700), image: 'https://images.unsplash.com/photo-1776347568367-e8f56443648f?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.9, reviews: 118 },
    { id: 'ne-2', name: 'Raymarine Quantum 2 Doppler Radar', brand: 'Raymarine', price: 3200, priceLabel: usd(3200), image: 'https://images.unsplash.com/photo-1576725198547-2ce386c431fd?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.8, reviews: 64 },
    { id: 'ne-3', name: 'B&G Zeus3S 16" Multifunction Display', brand: 'B&G', price: 4950, priceLabel: usd(4950), image: 'https://images.unsplash.com/photo-1600250940713-1d5c5bfb74cc?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.7, reviews: 48 },
    { id: 'ne-4', name: 'Icom M510 VHF with AIS', brand: 'Icom', price: 1200, priceLabel: usd(1200), image: 'https://images.unsplash.com/photo-1738869859618-959a1a4c381d?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.9, reviews: 87 },
  ],
  'safety-gear': [
    { id: 'sg-1', name: 'Viking RescYou Pro 8-Person Liferaft', brand: 'Viking', price: 4300, priceLabel: usd(4300), image: 'https://images.pexels.com/photos/8669061/pexels-photo-8669061.jpeg?auto=compress&cs=tinysrgb&w=1000', rating: 5.0, reviews: 29, badge: 'SOLAS' },
    { id: 'sg-2', name: 'Ocean Signal rescueME EPIRB1', brand: 'Ocean Signal', price: 580, priceLabel: usd(580), image: 'https://images.unsplash.com/photo-1601919972568-768b9bbcf548?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.9, reviews: 142 },
    { id: 'sg-3', name: 'Spinlock Deckvest 6D Lifejacket', brand: 'Spinlock', price: 320, priceLabel: usd(320), image: 'https://images.unsplash.com/photo-1647534398644-3deed08837b5?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.8, reviews: 210 },
    { id: 'sg-4', name: 'Offshore Medical Kit (Category A)', brand: 'Ocean Medical', price: 890, priceLabel: usd(890), image: 'https://images.pexels.com/photos/16135856/pexels-photo-16135856.jpeg?auto=compress&cs=tinysrgb&w=1000', rating: 4.7, reviews: 36 },
  ],
  'anchoring-mooring': [
    { id: 'am-1', name: 'Ultra Marine 35kg Stainless Anchor', brand: 'Ultra', price: 2100, priceLabel: usd(2100), image: 'https://images.unsplash.com/photo-1601919972568-768b9bbcf548?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.9, reviews: 58 },
    { id: 'am-2', name: 'Maxwell RC10-10 Windlass', brand: 'Maxwell', price: 3800, priceLabel: usd(3800), image: 'https://images.unsplash.com/photo-1523559094051-53bac879eb80?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.8, reviews: 34 },
    { id: 'am-3', name: 'Grade 70 Calibrated Chain (100m)', brand: 'Acco', price: 1950, priceLabel: usd(1950), image: 'https://images.pexels.com/photos/8669061/pexels-photo-8669061.jpeg?auto=compress&cs=tinysrgb&w=1000', rating: 4.7, reviews: 22 },
    { id: 'am-4', name: 'Polyform G7 Inflatable Fender', brand: 'Polyform', price: 420, priceLabel: usd(420), image: 'https://images.unsplash.com/photo-1596480330200-5794a8d4c942?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.8, reviews: 95 },
  ],
  'deck-equipment': [
    { id: 'de-1', name: 'Harken 80.2 STP Electric Winch', brand: 'Harken', price: 7400, priceLabel: usd(7400), image: 'https://images.pexels.com/photos/16135856/pexels-photo-16135856.jpeg?auto=compress&cs=tinysrgb&w=1000', rating: 4.9, reviews: 21 },
    { id: 'de-2', name: 'Lewmar Ocean 60 Hatch', brand: 'Lewmar', price: 890, priceLabel: usd(890), image: 'https://images.unsplash.com/photo-1596480330200-5794a8d4c942?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.8, reviews: 48 },
    { id: 'de-3', name: 'Besenzoni P460 Passarelle', brand: 'Besenzoni', price: 12800, priceLabel: usd(12800), image: 'https://images.unsplash.com/photo-1641787424891-63baaf4eef09?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.9, reviews: 14 },
    { id: 'de-4', name: 'Stainless Folding Cleat 10"', brand: 'Accon', price: 210, priceLabel: usd(210), image: 'https://images.unsplash.com/photo-1739973276975-e013eba151aa?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.7, reviews: 88 },
  ],
  'water-toys-tenders': [
    { id: 'wt-1', name: 'Seabob F5 SR Underwater Scooter', brand: 'Seabob', price: 14900, priceLabel: usd(14900), image: 'https://images.pexels.com/photos/10256590/pexels-photo-10256590.jpeg?auto=compress&cs=tinysrgb&w=1000', rating: 4.9, reviews: 37, badge: 'Hot' },
    { id: 'wt-2', name: 'Williams Sportjet 435 Tender', brand: 'Williams', price: 72000, priceLabel: usd(72000), image: 'https://images.unsplash.com/photo-1596480330200-5794a8d4c942?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 5.0, reviews: 12 },
    { id: 'wt-3', name: 'Yamaha WaveRunner FX Cruiser SVHO', brand: 'Yamaha', price: 21500, priceLabel: usd(21500), image: 'https://images.unsplash.com/photo-1647534398644-3deed08837b5?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.8, reviews: 54 },
    { id: 'wt-4', name: 'Red Paddle 12\'6 Sport MSL SUP', brand: 'Red Paddle', price: 1450, priceLabel: usd(1450), image: 'https://images.pexels.com/photos/27498639/pexels-photo-27498639.jpeg?auto=compress&cs=tinysrgb&w=1000', rating: 4.9, reviews: 203 },
  ],
  'interior-galley': [
    { id: 'ig-1', name: 'Isotherm Cruise 130 Drawer Fridge', brand: 'Isotherm', price: 1950, priceLabel: usd(1950), image: 'https://images.unsplash.com/photo-1596480330200-5794a8d4c942?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.7, reviews: 44 },
    { id: 'ig-2', name: 'Miele Combination Steam Oven (Marine)', brand: 'Miele', price: 5200, priceLabel: usd(5200), image: 'https://images.unsplash.com/photo-1686834097692-8beb8917385e?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.9, reviews: 18 },
    { id: 'ig-3', name: 'Frette 400TC Superyacht Linen Set', brand: 'Frette', price: 980, priceLabel: usd(980), image: 'https://images.unsplash.com/photo-1700563061894-99a4aa83d048?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.8, reviews: 67 },
    { id: 'ig-4', name: 'Riedel Superleggero Decanter Set', brand: 'Riedel', price: 640, priceLabel: usd(640), image: 'https://images.unsplash.com/photo-1641787424891-63baaf4eef09?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.9, reviews: 51 },
  ],
  'maintenance-cleaning': [
    { id: 'mc-1', name: 'International Micron 66 Antifouling (5L)', brand: 'International', price: 320, priceLabel: usd(320), image: 'https://images.unsplash.com/photo-1739973276975-e013eba151aa?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.8, reviews: 189 },
    { id: 'mc-2', name: 'Starbrite Premium Marine Polish', brand: 'Starbrite', price: 85, priceLabel: usd(85), image: 'https://images.unsplash.com/photo-1552656967-7a0991a13906?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.9, reviews: 412 },
    { id: 'mc-3', name: '3M Perfect-It Gelcoat Compound Kit', brand: '3M', price: 180, priceLabel: usd(180), image: 'https://images.pexels.com/photos/16135856/pexels-photo-16135856.jpeg?auto=compress&cs=tinysrgb&w=1000', rating: 4.8, reviews: 152 },
    { id: 'mc-4', name: 'IMS Pro Teak Deck Cleaning Kit', brand: 'IMS', price: 140, priceLabel: usd(140), image: 'https://images.unsplash.com/photo-1596480330200-5794a8d4c942?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.7, reviews: 98 },
  ],
  'electrical-plumbing': [
    { id: 'el-1', name: 'Victron Lithium 300Ah Smart Battery', brand: 'Victron', price: 3800, priceLabel: usd(3800), image: 'https://images.unsplash.com/photo-1523559094051-53bac879eb80?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.9, reviews: 167 },
    { id: 'el-2', name: 'Mastervolt MassCombi 12/3500-150', brand: 'Mastervolt', price: 4200, priceLabel: usd(4200), image: 'https://images.unsplash.com/photo-1552656967-7a0991a13906?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.8, reviews: 72 },
    { id: 'el-3', name: 'Headhunter Mach 5 Water Pump', brand: 'Headhunter', price: 1650, priceLabel: usd(1650), image: 'https://images.pexels.com/photos/8669061/pexels-photo-8669061.jpeg?auto=compress&cs=tinysrgb&w=1000', rating: 4.7, reviews: 33 },
    { id: 'el-4', name: 'Dometic MasterFlush 8109 Toilet', brand: 'Dometic', price: 1250, priceLabel: usd(1250), image: 'https://images.unsplash.com/photo-1739973276975-e013eba151aa?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.6, reviews: 58 },
  ],
};

export const SERVICE_VENDORS = {
  'maintenance-refit': [
    { id: 'mr-1', name: 'Phuket Boat Lagoon Shipyard', location: 'Phuket, Thailand', price: null, priceLabel: 'Quote on request', image: 'https://images.pexels.com/photos/14282669/pexels-photo-14282669.jpeg?auto=compress&cs=tinysrgb&w=1000', rating: 4.8, reviews: 214, badge: 'Verified' },
    { id: 'mr-2', name: 'Ocean Marina Yacht Services', location: 'Pattaya, Thailand', price: null, priceLabel: 'From $120/hr', image: 'https://images.pexels.com/photos/16135856/pexels-photo-16135856.jpeg?auto=compress&cs=tinysrgb&w=1000', rating: 4.7, reviews: 98 },
    { id: 'mr-3', name: 'Straits Quay Marine (Penang)', location: 'Penang, Malaysia', price: null, priceLabel: 'From $95/hr', image: 'https://images.unsplash.com/photo-1641787424891-63baaf4eef09?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.6, reviews: 61 },
    { id: 'mr-4', name: 'Singapore Yacht Refit Centre', location: 'Singapore', price: null, priceLabel: 'From $180/hr', image: 'https://images.unsplash.com/photo-1775700489260-41ee85c86954?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.9, reviews: 134, badge: 'Premier' },
  ],
  'crew-recruitment': [
    { id: 'cr-1', name: 'Viking Crew Asia', location: 'Phuket, Thailand', price: null, priceLabel: 'Placement from 8%', image: 'https://images.unsplash.com/photo-1700563061894-99a4aa83d048?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.8, reviews: 74 },
    { id: 'cr-2', name: 'YPI Crew Southeast Asia', location: 'Singapore', price: null, priceLabel: 'Placement from 10%', image: 'https://images.unsplash.com/photo-1686834097692-8beb8917385e?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.9, reviews: 52 },
    { id: 'cr-3', name: 'Bluewater Crew Asia', location: 'Phuket, Thailand', price: null, priceLabel: 'Placement from 9%', image: 'https://images.unsplash.com/photo-1601919972568-768b9bbcf548?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.7, reviews: 63 },
    { id: 'cr-4', name: 'Crew Pacific', location: 'Langkawi, Malaysia', price: null, priceLabel: 'Contact for rates', image: 'https://images.unsplash.com/photo-1776347568367-e8f56443648f?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.6, reviews: 28 },
  ],
  'captain-crew-services': [
    { id: 'cs-1', name: 'Capt. Marcus Holloway (200GT)', location: 'Phuket / SEA', price: null, priceLabel: '$450/day', image: 'https://images.unsplash.com/photo-1686834097692-8beb8917385e?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 5.0, reviews: 41 },
    { id: 'cs-2', name: 'Freelance Chef — Sophie Laurent', location: 'Singapore / SEA', price: null, priceLabel: '$600/day', image: 'https://images.unsplash.com/photo-1700563061894-99a4aa83d048?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.9, reviews: 29 },
    { id: 'cs-3', name: 'Delivery Skipper Network SEA', location: 'Regional', price: null, priceLabel: 'From $380/day', image: 'https://images.unsplash.com/photo-1601919972568-768b9bbcf548?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.7, reviews: 82 },
    { id: 'cs-4', name: 'Relief Stewardess Agency', location: 'Langkawi', price: null, priceLabel: 'From $220/day', image: 'https://images.unsplash.com/photo-1647534398644-3deed08837b5?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.8, reviews: 36 },
  ],
  'insurance-finance': [
    { id: 'if-1', name: 'Pantaenius Yacht Insurance', location: 'Global — SEA desk', price: null, priceLabel: 'Quote in 24h', image: 'https://images.unsplash.com/photo-1633123454005-28016323abc2?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.9, reviews: 312, badge: 'Tier 1' },
    { id: 'if-2', name: 'EFG Yacht Finance', location: 'Singapore', price: null, priceLabel: 'Loans from $500k', image: 'https://images.unsplash.com/photo-1775700489260-41ee85c86954?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.8, reviews: 47 },
    { id: 'if-3', name: 'Alexforbes Marine', location: 'Global', price: null, priceLabel: 'Hull & charter liability', image: 'https://images.unsplash.com/photo-1641787424891-63baaf4eef09?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.7, reviews: 88 },
    { id: 'if-4', name: 'Credit Suisse Yacht Finance', location: 'APAC', price: null, priceLabel: 'Bespoke structuring', image: 'https://images.unsplash.com/photo-1598887142487-3c854d51eaa7?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.8, reviews: 22 },
  ],
  'survey-valuation': [
    { id: 'sv-1', name: 'Ward & Partners Marine Surveyors', location: 'Phuket', price: null, priceLabel: 'From $1,200', image: 'https://images.unsplash.com/photo-1576725198547-2ce386c431fd?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.9, reviews: 96 },
    { id: 'sv-2', name: 'Asia Yacht Surveyors (IIMS)', location: 'Singapore', price: null, priceLabel: 'From $1,500', image: 'https://images.unsplash.com/photo-1600250940713-1d5c5bfb74cc?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.8, reviews: 73 },
    { id: 'sv-3', name: 'Marine Surveys Langkawi', location: 'Langkawi', price: null, priceLabel: 'From $1,100', image: 'https://images.unsplash.com/photo-1641787424891-63baaf4eef09?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.7, reviews: 34 },
    { id: 'sv-4', name: 'Superyacht Valuations Asia', location: 'Regional', price: null, priceLabel: 'Valuation from $2,500', image: 'https://images.unsplash.com/photo-1633123454005-28016323abc2?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.9, reviews: 41 },
  ],
  'marina-berthing': [
    { id: 'mb-1', name: 'Phuket Yacht Haven Marina', location: 'Phuket, Thailand', price: null, priceLabel: 'From $25/ft/mo', image: 'https://images.pexels.com/photos/14282669/pexels-photo-14282669.jpeg?auto=compress&cs=tinysrgb&w=1000', rating: 4.8, reviews: 208 },
    { id: 'mb-2', name: 'Rebak Island Marina', location: 'Langkawi, Malaysia', price: null, priceLabel: 'From $22/ft/mo', image: 'https://images.pexels.com/photos/14821387/pexels-photo-14821387.jpeg?auto=compress&cs=tinysrgb&w=1000', rating: 4.7, reviews: 142 },
    { id: 'mb-3', name: 'ONE°15 Marina Sentosa Cove', location: 'Singapore', price: null, priceLabel: 'From $48/ft/mo', image: 'https://images.unsplash.com/photo-1596658817095-21feff6f586f?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.9, reviews: 187, badge: 'Premium' },
    { id: 'mb-4', name: 'Puteri Harbour Marina', location: 'Johor, Malaysia', price: null, priceLabel: 'From $18/ft/mo', image: 'https://images.unsplash.com/photo-1669574035244-86284e52446f?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.6, reviews: 96 },
  ],
  'transport-delivery': [
    { id: 'td-1', name: 'Sevenstar Yacht Transport', location: 'Global / SEA hub', price: null, priceLabel: 'Quote on route', image: 'https://images.unsplash.com/photo-1759950968587-8ac44e499dfa?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.8, reviews: 144 },
    { id: 'td-2', name: 'DYT Yacht Transport', location: 'Global', price: null, priceLabel: 'Quote on route', image: 'https://images.unsplash.com/photo-1768740615503-52872b6707b1?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.9, reviews: 212 },
    { id: 'td-3', name: 'Asia Delivery Skippers Coop', location: 'Regional', price: null, priceLabel: 'From $380/day', image: 'https://images.unsplash.com/photo-1601919972568-768b9bbcf548?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.7, reviews: 67 },
    { id: 'td-4', name: 'Peters & May Yacht Logistics', location: 'Global', price: null, priceLabel: 'Full logistics', image: 'https://images.unsplash.com/photo-1775700489260-41ee85c86954?crop=entropy&cs=srgb&fm=jpg&w=1000&q=80', rating: 4.8, reviews: 98 },
  ],
};
